import { useRef, useCallback, useEffect } from 'react';

/**
 * Hook that manages an AbortController for cancelling in-flight requests.
 * Returns a function that creates a new AbortController while aborting any previous one.
 *
 * Usage:
 * ```
 * const { getAbortController } = useAbortController();
 *
 * const fetchData = async () => {
 *   const abortController = getAbortController();
 *   const response = await fetch(url, { signal: abortController.signal });
 *   // ...
 * };
 * ```
 */
export function useAbortController() {
  const abortControllerRef = useRef<AbortController | null>(null);

  // Create a new abort controller, aborting any previous one
  const getAbortController = useCallback(() => {
    // Atomic swap pattern: create new, save old, update ref, then abort old
    const newController = new AbortController();
    const previousController = abortControllerRef.current;
    abortControllerRef.current = newController;

    if (previousController) {
      previousController.abort();
    }

    return newController;
  }, []);

  // Abort on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return { getAbortController };
}
